import { useState, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  FormControl,
  Select,
  MenuItem,
  Typography,
  Skeleton,
  Alert,
  Button,
  Grid,
  TextField,
  Tooltip as MuiTooltip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  ReferenceLine,
} from 'recharts';
import { useDeviceMetrics, useDeviceHeartbeatCadence } from '@/hooks/use-device-analytics';
import type { AnalyticsWindow } from '@/lib/analytics-range';
import type { AggregatedMetricPoint, DeviceMetricPoint } from '@/types';
import { colors } from '@/theme/tokens';
import { metricColors } from '@/theme/health';
import { AccessibleChart, VisuallyHiddenTable } from '@/components/chart-a11y';

/** An event to mark on the metric charts, so cause and effect share an x-axis. */
export interface MetricMarker {
  ts: string;
  label: string;
  color: string;
}

interface DeviceMetricsProps {
  deviceId: string;
  /**
   * Shared Analytics-tab window. When supplied the panel drops its own range
   * selector and follows the container's; omit it and it behaves as before.
   */
  window?: AnalyticsWindow;
  /** Events drawn as vertical markers on the CPU and memory charts. */
  markers?: MetricMarker[];
}

type TimeRange = '1h' | '6h' | '24h' | '7d' | '30d' | 'custom';

const TIME_RANGE_CONFIG: Record<Exclude<TimeRange, 'custom'>, { label: string; interval: string; ms: number }> = {
  '1h':  { label: 'Last 1h',  interval: '1m',  ms: 60 * 60 * 1000 },
  '6h':  { label: 'Last 6h',  interval: '5m',  ms: 6 * 60 * 60 * 1000 },
  '24h': { label: 'Last 24h', interval: '15m', ms: 24 * 60 * 60 * 1000 },
  '7d':  { label: 'Last 7d',  interval: '1h',  ms: 7 * 24 * 60 * 60 * 1000 },
  '30d': { label: 'Last 30d', interval: '6h',  ms: 30 * 24 * 60 * 60 * 1000 },
};

// Pick a sensible aggregation bucket for an arbitrary span so the custom
// range stays readable without overwhelming the chart with points.
function intervalForSpan(spanMs: number): string {
  const hours = spanMs / (60 * 60 * 1000);
  if (hours <= 2) return '1m';
  if (hours <= 12) return '5m';
  if (hours <= 48) return '15m';
  if (hours <= 24 * 14) return '1h';
  if (hours <= 24 * 60) return '6h';
  return '24h';
}

// intervalMs parses the bucket-interval strings used above ('1m'/'15m'/'1h'/…).
function intervalMs(s: string): number {
  const m = /^(\d+)([mhd])$/.exec(s);
  if (!m) return 0;
  const n = Number(m[1]);
  return n * (m[2] === 'm' ? 60_000 : m[2] === 'h' ? 3_600_000 : 86_400_000);
}

// CADENCE_MIN_INTERVAL_MS mirrors the server's metrics_agg_interval (#359):
// device_metrics is aggregated into 30-min buckets, so heartbeat cadence can't
// be resolved finer than that without showing phantom gaps.
const CADENCE_MIN_INTERVAL_MS = 30 * 60 * 1000;

// datetime-local <input> emits "YYYY-MM-DDTHH:mm" without timezone; treat as local.
function localInputToISO(local: string): string {
  return new Date(local).toISOString();
}
function isoToLocalInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatTimestamp(ts: string, longForm: boolean): string {
  const d = new Date(ts);
  if (longForm) {
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

// Connectivity state per cadence bucket. OFF (no beats) is the neutral, healthy
// default for a fitness machine — never red. Red (dropout) is reserved for beats
// lost WHILE the device was online; that is the only real fault on this chart.
type CadenceState = 'off' | 'on' | 'light' | 'dropout';
const CADENCE_COLORS: Record<CadenceState, string> = {
  off: colors.grey[200],
  on: colors.success.main,
  light: colors.warning.main,
  dropout: colors.error.main,
};
const CADENCE_LABELS: Record<CadenceState, string> = {
  off: 'Off / idle',
  on: 'On cadence',
  light: 'Light loss',
  dropout: 'Dropout',
};

function humanizeDuration(sec: number): string {
  if (sec <= 0) return '0s';
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  if (m < 60) return s ? `${m}m ${s}s` : `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

function isAggregated(
  data: (DeviceMetricPoint | AggregatedMetricPoint)[]
): data is AggregatedMetricPoint[] {
  return data.length > 0 && 'avg_cpu_percent' in data[0];
}

export function DeviceMetrics({ deviceId, window: win, markers }: DeviceMetricsProps) {
  const [timeRange, setTimeRange] = useState<TimeRange>('6h');
  const embedded = !!win;

  // Custom range state (only used when timeRange === 'custom').
  // Default the inputs to the previous canned window so first switch makes sense.
  const [customStart, setCustomStart] = useState<string>(() =>
    isoToLocalInput(new Date(Date.now() - TIME_RANGE_CONFIG['6h'].ms).toISOString())
  );
  const [customEnd, setCustomEnd] = useState<string>(() =>
    isoToLocalInput(new Date().toISOString())
  );

  const isCustom = !embedded && timeRange === 'custom';

  // Pin the window per range/custom-inputs so the query key is stable;
  // computing dates inline causes infinite refetch loops.
  const own = useMemo(() => {
    if (isCustom) {
      const startIso = localInputToISO(customStart);
      const endIso = localInputToISO(customEnd);
      const span = new Date(endIso).getTime() - new Date(startIso).getTime();
      return { start: startIso, end: endIso, interval: intervalForSpan(span) };
    }
    const cfg = TIME_RANGE_CONFIG[timeRange === 'custom' ? '6h' : timeRange];
    const now = new Date();
    return {
      start: new Date(now.getTime() - cfg.ms).toISOString(),
      end: now.toISOString(),
      interval: cfg.interval,
    };
  }, [isCustom, customStart, customEnd, timeRange]);

  const start = win?.start ?? own.start;
  const end = win?.end ?? own.end;
  const interval = win?.interval ?? own.interval;

  const customRangeValid = !isCustom || new Date(end).getTime() > new Date(start).getTime();
  const longForm = win
    ? win.longForm
    : isCustom
      ? new Date(end).getTime() - new Date(start).getTime() >= 24 * 60 * 60 * 1000
      : timeRange === '7d' || timeRange === '30d';

  const { data: metricsResponse, isLoading, error, refetch: refetchMetrics } = useDeviceMetrics(deviceId, {
    start,
    end,
    interval,
  }, { enabled: customRangeValid });

  // Heartbeat cadence shares the window with the CPU/memory chart, but its
  // interval is floored at the server aggregation resolution: device_metrics
  // is bucketed into 30-min rows (#359), so a finer cadence bucket would leave
  // most sub-buckets falsely empty (phantom gaps). We floor the cadence
  // interval independently rather than coarsen the CPU/memory chart.
  // The API also caps windows at 14d (matching retention) — skip longer ranges
  // instead of rendering a guaranteed error.
  const cadenceInterval =
    win?.cadenceInterval ?? (intervalMs(interval) >= CADENCE_MIN_INTERVAL_MS ? interval : '30m');
  const cadenceEligible =
    customRangeValid &&
    (win
      ? win.cadenceEligible
      : new Date(end).getTime() - new Date(start).getTime() <= 14 * 24 * 60 * 60 * 1000);
  const { data: cadence, error: cadenceError, isLoading: cadenceLoading, refetch: refetchCadence } = useDeviceHeartbeatCadence(deviceId, {
    start,
    end,
    interval: cadenceInterval,
  }, { enabled: cadenceEligible });

  // Per-bucket connectivity state (off/on/light/dropout), idle treated as the
  // neutral default — see CADENCE_COLORS. The state is the non-color channel
  // (mirrored into the legend + a11y table).
  const cadenceChartData = useMemo(() => {
    if (!cadence?.buckets?.length) return [];
    return cadence.buckets.map((b) => {
      const online = b.beat_count > 0;
      const lossRatio = b.expected_beats > 0 ? b.missed_beats / b.expected_beats : 0;
      const state: CadenceState = !online
        ? 'off'
        : b.missed_beats === 0
          ? 'on'
          : lossRatio >= 0.5
            ? 'dropout'
            : 'light';
      return {
        time: formatTimestamp(b.timestamp, longForm),
        ts: b.timestamp,
        beats: b.beat_count,
        expected: b.expected_beats,
        missed: b.missed_beats,
        maxInterval: Math.round(b.max_interval_seconds),
        state,
        color: CADENCE_COLORS[state],
      };
    });
  }, [cadence, longForm]);

  // Window stats computed over ONLINE buckets only — idle never counts as a
  // shortfall. coverageActive is null when the device was off the whole window.
  const cadenceStats = useMemo(() => {
    const online = cadenceChartData.filter((b) => b.state !== 'off');
    const sumBeats = online.reduce((s, b) => s + b.beats, 0);
    const sumExpected = online.reduce((s, b) => s + b.expected, 0);
    return {
      onlineCount: online.length,
      totalCount: cadenceChartData.length,
      coverageActive: sumExpected > 0 ? (sumBeats / sumExpected) * 100 : null,
      dropouts: cadenceChartData.filter((b) => b.state === 'dropout').length,
      worstGap: cadenceChartData.reduce((m, b) => Math.max(m, b.maxInterval), 0),
    };
  }, [cadenceChartData]);

  // Run-length-encode adjacent same-state buckets into strip segments: long idle
  // stretches collapse to one wide grey segment (readable + few DOM nodes).
  const cadenceSegments = useMemo(() => {
    const segs: { state: CadenceState; color: string; count: number; start: string; end: string }[] = [];
    for (const b of cadenceChartData) {
      const last = segs[segs.length - 1];
      if (last && last.state === b.state) {
        last.count += 1;
        last.end = b.time;
      } else {
        segs.push({ state: b.state, color: b.color, count: 1, start: b.time, end: b.time });
      }
    }
    return segs;
  }, [cadenceChartData]);

  const chartData = useMemo(() => {
    const rawData = metricsResponse?.data;
    if (!rawData || rawData.length === 0) return [];

    if (isAggregated(rawData)) {
      return [...rawData]
        .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
        .map((m) => ({
          time: formatTimestamp(m.timestamp, longForm),
          ts: m.timestamp,
          cpu: Math.round(m.avg_cpu_percent * 100) / 100,
          cpuMax: Math.round(m.max_cpu_percent * 100) / 100,
          memoryMB: Math.round(m.avg_memory_kb / 1024 * 100) / 100,
          memoryMaxMB: Math.round(m.max_memory_kb / 1024 * 100) / 100,
          samples: m.sample_count,
        }));
    }

    // Raw metrics (no aggregation)
    return [...rawData]
      .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
      .map((m) => ({
        time: formatTimestamp(m.timestamp, longForm),
        ts: m.timestamp,
        cpu: Math.round(m.cpu_percent * 100) / 100,
        memoryMB: Math.round(m.memory_kb / 1024 * 100) / 100,
      }));
  }, [metricsResponse, longForm]);

  // Snap each event to its nearest plotted bucket. Recharts' XAxis here is
  // categorical (dataKey="time"), so a ReferenceLine has to name an existing
  // category — an exact timestamp would simply not render. Events further than
  // one bucket from any point fall outside the plotted span and are dropped.
  const chartMarkers = useMemo(() => {
    if (!markers?.length || chartData.length === 0) return [];
    const tolerance = Math.max(intervalMs(interval), 60_000);
    const times = chartData.map((d) => new Date(d.ts).getTime());
    const seen = new Set<string>();
    const out: { x: string; label: string; color: string }[] = [];
    for (const m of markers) {
      const t = new Date(m.ts).getTime();
      let best = 0;
      let bestDelta = Infinity;
      for (let i = 0; i < times.length; i++) {
        const delta = Math.abs(times[i] - t);
        if (delta < bestDelta) { bestDelta = delta; best = i; }
      }
      if (bestDelta > tolerance) continue;
      // One line per bucket per severity — a burst of reboots in one bucket
      // would otherwise stack identical lines on the same pixel.
      const key = `${chartData[best].time}|${m.color}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ x: chartData[best].time, label: m.label, color: m.color });
    }
    return out;
  }, [markers, chartData, interval]);

  const referenceLines = chartMarkers.map((m) => (
    <ReferenceLine
      key={`${m.x}-${m.color}`}
      x={m.x}
      stroke={m.color}
      strokeDasharray="3 3"
      strokeOpacity={0.75}
      ifOverflow="extendDomain"
      label={{ value: '', position: 'top' }}
    />
  ));

  return (
    <Box>
      {/* Time Range Selector — hidden when the Analytics tab owns the range. */}
      {!embedded && (
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="subtitle2" color="text.secondary">
            Time Range:
          </Typography>
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <Select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value as TimeRange)}
              inputProps={{ 'aria-label': 'Time range' }}
            >
              {(Object.keys(TIME_RANGE_CONFIG) as Array<Exclude<TimeRange, 'custom'>>).map((key) => (
                <MenuItem key={key} value={key}>{TIME_RANGE_CONFIG[key].label}</MenuItem>
              ))}
              <MenuItem value="custom">Custom range…</MenuItem>
            </Select>
          </FormControl>

          {isCustom && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                type="datetime-local"
                size="small"
                label="From"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
              />
              <TextField
                type="datetime-local"
                size="small"
                label="To"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Box>
          )}
        </Box>
      )}

      {isCustom && !customRangeValid && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          End time must be after start time.
        </Alert>
      )}

      {/* Skeletons in the charts' own shape — a centred spinner gave no hint of
          what was coming and collapsed the layout, so the page jumped once the
          data landed. */}
      {isLoading && (
        <Grid container spacing={2}>
          {['CPU Usage', 'Memory Usage'].map((title) => (
            <Grid key={title} size={{ xs: 12, lg: 6 }}>
              <Card variant="outlined">
                <CardHeader
                  title={title}
                  titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
                  sx={{ pb: 0 }}
                />
                <CardContent>
                  <Skeleton variant="rounded" height={250} />
                </CardContent>
              </Card>
            </Grid>
          ))}
          <Grid size={12}>
            <Card variant="outlined">
              <CardHeader
                title="Heartbeat Cadence"
                titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
                sx={{ pb: 0 }}
              />
              <CardContent>
                <Skeleton variant="rounded" height={140} />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => refetchMetrics()}>
              Retry
            </Button>
          }
        >
          Failed to load metrics. Please try again.
        </Alert>
      )}

      {!isLoading && !error && chartData.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No metrics data available for the selected time range.
        </Alert>
      )}

      {chartData.length > 0 && 'cpuMax' in (chartData[0] || {}) && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mb: 1.5, lineHeight: 1.4 }}
        >
          Each point is a <strong>{interval}</strong> bucket.
          {' '}<strong>Avg</strong> = mean across samples in the bucket (overall load).
          {' '}<strong>Max</strong> = peak sample in the bucket — catches short spikes the average smooths over.
        </Typography>
      )}

      {chartData.length > 0 && (
        <Grid container spacing={2}>
          {/* CPU Usage Chart */}
          <Grid size={{ xs: 12, lg: 6 }}>
            <Card variant="outlined">
              <CardHeader
                title="CPU Usage"
                titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
                sx={{ pb: 0 }}
              />
              <CardContent>
                <AccessibleChart
                  label="CPU usage over time"
                  description={`Average CPU percent per ${interval} bucket`}
                  sx={{ height: 250 }}
                >
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      {referenceLines}
                      <XAxis
                        dataKey="time"
                        tick={{ fontSize: 11 }}
                        interval="preserveStartEnd"
                      />
                      <YAxis
                        domain={[0, 100]}
                        tick={{ fontSize: 11 }}
                        tickFormatter={(v) => `${v}%`}
                      />
                      <Tooltip
                        // recharts passes the series `name` prop ("Avg CPU"/"Max CPU").
                        formatter={(value: number, name: string) => [`${value}%`, name]}
                      />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="cpu"
                        name="Avg CPU"
                        stroke={metricColors.cpu}
                        strokeWidth={2}
                        dot={false}
                        activeDot={{ r: 4 }}
                        connectNulls={false}
                      />
                      {'cpuMax' in (chartData[0] || {}) && (
                        <Line
                          type="monotone"
                          dataKey="cpuMax"
                          name="Max CPU"
                          stroke={alpha(metricColors.cpu, 0.85)}
                          strokeWidth={1.5}
                          strokeDasharray="4 2"
                          dot={false}
                          connectNulls={false}
                        />
                      )}
                    </LineChart>
                  </ResponsiveContainer>
                </AccessibleChart>
              </CardContent>
            </Card>
          </Grid>

          {/* Memory Usage Chart */}
          <Grid size={{ xs: 12, lg: 6 }}>
            <Card variant="outlined">
              <CardHeader
                title="Memory Usage"
                titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
                sx={{ pb: 0 }}
              />
              <CardContent>
                <AccessibleChart
                  label="Memory usage over time"
                  description={`Average memory in MB per ${interval} bucket`}
                  sx={{ height: 250 }}
                >
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      {referenceLines}
                      <XAxis
                        dataKey="time"
                        tick={{ fontSize: 11 }}
                        interval="preserveStartEnd"
                      />
                      <YAxis
                        tick={{ fontSize: 11 }}
                        tickFormatter={(v) => `${v} MB`}
                      />
                      <Tooltip
                        // recharts passes the series `name` prop ("Avg Memory"/"Max Memory").
                        formatter={(value: number, name: string) => [`${value} MB`, name]}
                      />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="memoryMB"
                        name="Avg Memory"
                        stroke={metricColors.memory}
                        strokeWidth={2}
                        dot={false}
                        activeDot={{ r: 4 }}
                        connectNulls={false}
                      />
                      {'memoryMaxMB' in (chartData[0] || {}) && (
                        <Line
                          type="monotone"
                          dataKey="memoryMaxMB"
                          name="Max Memory"
                          stroke={alpha(metricColors.memory, 0.85)}
                          strokeWidth={1.5}
                          strokeDasharray="4 2"
                          dot={false}
                          connectNulls={false}
                        />
                      )}
                    </LineChart>
                  </ResponsiveContainer>
                </AccessibleChart>
              </CardContent>
            </Card>
          </Grid>

        </Grid>
      )}

      {/* Heartbeat cadence — beat/missed counts per bucket vs the nominal
          rate. Zero-beat windows still chart (all-missed bars ARE the outage
          view); a distinct error state is reserved for query failure only. */}
      {!cadenceEligible && customRangeValid && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          Heartbeat cadence is available for ranges up to 14 days.
        </Typography>
      )}
      {cadenceEligible && cadenceLoading && !cadence && (
        <Skeleton variant="rounded" height={120} sx={{ mt: 1 }} />
      )}
      {cadenceError != null && (
        <Alert
          severity="error"
          sx={{ mt: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => refetchCadence()}>
              Retry
            </Button>
          }
        >
          Failed to load heartbeat cadence.
        </Alert>
      )}
      {cadence && cadenceChartData.length > 0 && (
        <Card variant="outlined" sx={{ mt: 2 }}>
          <CardHeader
            title="Heartbeat Cadence"
            titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
            sx={{ pb: 0 }}
            action={
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', pt: 0.5 }}>
                {cadence.degraded ? (
                  <Chip size="small" color="error" label="Crash-loop: beating >4× nominal" />
                ) : cadenceStats.coverageActive == null ? (
                  <Chip size="small" variant="outlined" label="Off this period" />
                ) : (
                  <Chip
                    size="small"
                    variant="outlined"
                    color={cadenceStats.coverageActive >= 99 ? 'success' : cadenceStats.coverageActive >= 90 ? 'warning' : 'error'}
                    label={`Coverage (active) ${cadenceStats.coverageActive.toFixed(1)}%`}
                  />
                )}
                <Chip size="small" variant="outlined" label={`${cadence.total_beats.toLocaleString()} beats`} />
                <Chip
                  size="small"
                  variant="outlined"
                  color={cadenceStats.dropouts > 0 ? 'error' : 'default'}
                  label={`Longest gap ${humanizeDuration(cadenceStats.worstGap)}`}
                />
              </Box>
            }
          />
          <CardContent>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mb: 1, lineHeight: 1.4 }}
            >
              Connectivity per <strong>{interval}</strong> bucket. Off/idle is normal (machine not in use);
              red marks beats lost <strong>while the machine was online</strong>.
            </Typography>
            {/* Swatch + word legend — the glyph/word is the non-color channel (WCAG 1.4.1). */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
              <Chip size="small" variant="outlined" label="On cadence"
                icon={<Box component="span" aria-hidden sx={{ color: CADENCE_COLORS.on, fontSize: 14, ml: 0.75 }}>●</Box>} />
              <Chip size="small" variant="outlined" label="Light loss"
                icon={<Box component="span" aria-hidden sx={{ color: CADENCE_COLORS.light, fontSize: 14, ml: 0.75 }}>◐</Box>} />
              <Chip size="small" variant="outlined" label="Dropout"
                icon={<Box component="span" aria-hidden sx={{ color: CADENCE_COLORS.dropout, fontSize: 14, ml: 0.75 }}>○</Box>} />
              <Chip size="small" variant="outlined" label="Off / idle"
                icon={<Box component="span" aria-hidden sx={{ width: 11, height: 11, ml: 0.75, borderRadius: 0.5, bgcolor: CADENCE_COLORS.off }} />} />
            </Box>
            {/* Connectivity state strip — reads like an uptime/Gantt bar: long grey
                = idle (fine), red = a real dropout while online. */}
            <Box
              role="img"
              aria-label={`Heartbeat connectivity over time: ${cadenceStats.onlineCount} of ${cadenceStats.totalCount} buckets online${cadenceStats.dropouts ? `, ${cadenceStats.dropouts} dropout${cadenceStats.dropouts === 1 ? '' : 's'}` : ''}.`}
              sx={{ display: 'flex', height: 28, borderRadius: 1, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}
            >
              {cadenceSegments.map((seg, i) => (
                <MuiTooltip
                  key={i}
                  title={`${CADENCE_LABELS[seg.state]} · ${seg.start}${seg.end !== seg.start ? `–${seg.end}` : ''} · ${seg.count} bucket${seg.count === 1 ? '' : 's'}`}
                >
                  <Box sx={{ flex: seg.count, minWidth: 2, bgcolor: seg.color }} />
                </MuiTooltip>
              ))}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
              <Typography variant="caption" color="text.secondary">{cadenceChartData[0]?.time}</Typography>
              <Typography variant="caption" color="text.secondary">{cadenceChartData[cadenceChartData.length - 1]?.time}</Typography>
            </Box>
            <VisuallyHiddenTable
              caption="Heartbeat cadence by bucket"
              columns={['Bucket', 'State', 'Beats', 'Expected', 'Missed', 'Longest gap (s)']}
              rows={cadenceChartData.map((d) => [d.time, CADENCE_LABELS[d.state], d.beats, d.expected, d.missed, d.maxInterval])}
            />
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
